import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Car, Eye, EyeOff, User, KeyRound, Shield, UserCircle, KeySquare, Crown, Save } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../utils/api';


export default function Login({ forceLoginMode = null }) {
  const navigate = useNavigate();
  const [loginMode, setLoginMode] = useState('select'); // 'select', 'admin', 'user', 'super_admin'
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (forceLoginMode) setLoginMode(forceLoginMode);
  }, [forceLoginMode]);
  const [loading, setLoading] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [rememberCode, setRememberCode] = useState(() => !!localStorage.getItem('saved_tenant_code'));
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
    tenant_code: localStorage.getItem('saved_tenant_code') || '',
  });

  const handlePasswordLogin = async () => {
    if (!credentials.username.trim() || !credentials.password.trim()) {
      toast.error('Παρακαλώ συμπληρώστε όλα τα πεδία');
      return;
    }

    // Tenant code required for admin/user modes (not for default parking)
    setLoading(true);
    try {
      const response = await apiFetch(`/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: credentials.username,
          password: credentials.password,
          tenant_code: credentials.tenant_code || '',
        })
      });

      if (response.status === 429) {
        toast.error('Πολλές αποτυχημένες προσπάθειες. Δοκιμάστε ξανά σε 15 λεπτά.');
        return;
      }

      if (!response.ok) {
        // If admin login fails, try SuperAdmin login silently
        try {
          const saResp = await apiFetch(`/api/super-admin/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              username: credentials.username,
              password: credentials.password,
            })
          });
          if (saResp.ok) {
            localStorage.setItem('is_super_admin', 'true');
            toast.success('Καλωσήρθατε, Super Admin!');
            window.location.assign('/super-admin');
            return;
          }
        } catch {}
        const error = await response.json();
        throw new Error(error.detail || 'Σφάλμα σύνδεσης');
      }

      const data = await response.json();

      // Check if must change password
      if (data.must_change_password) {
        // Cookie is already set by backend as HttpOnly — no need for token in JS
        setShowChangePassword(true);
        toast.info('Πρέπει να αλλάξετε τον κωδικό σας');
        return;
      }

      // Save or clear parking code based on user preference
      if (rememberCode && credentials.tenant_code) {
        localStorage.setItem('saved_tenant_code', credentials.tenant_code);
      } else {
        localStorage.removeItem('saved_tenant_code');
      }

      // Store tenant info (session cookie set by backend as HttpOnly)
      localStorage.setItem('tenant_id', data.tenant_id || 'default');
      if (data.tenant) {
        localStorage.setItem('tenant_features', JSON.stringify(data.tenant.features || []));
        localStorage.setItem('tenant_name', data.tenant.business_name || '');
      }

      toast.success(`Καλωσήρθατε, ${data.user.name}!`);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message || 'Σφάλμα σύνδεσης');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error('Συμπληρώστε και τα δύο πεδία');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Οι κωδικοί δεν ταιριάζουν');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Ο κωδικός πρέπει να είναι τουλάχιστον 6 χαρακτήρες');
      return;
    }

    setLoading(true);
    try {
      const response = await apiFetch(`/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          current_password: credentials.password,
          new_password: newPassword,
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Σφάλμα αλλαγής κωδικού');
      }

      toast.success('Ο κωδικός άλλαξε! Καλωσήρθατε!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotKey = async () => {
    const email = prompt('Εισάγετε το email που είναι καταχωρημένο στο parking σας:');
    if (!email || !email.trim()) return;
    try {
      const response = await apiFetch(`/api/auth/forgot-key`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || 'Το κλειδί στάλθηκε στο email σας');
      } else {
        toast.error(data.detail || 'Σφάλμα');
      }
    } catch {
      toast.error('Σφάλμα σύνδεσης');
    }
  };

  const handleSuperAdminLogin = async () => {
    if (!credentials.username.trim() || !credentials.password.trim()) {
      toast.error('Παρακαλώ συμπληρώστε τα πεδία');
      return;
    }

    setLoading(true);
    try {
      const response = await apiFetch(`/api/super-admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: credentials.username,
          password: credentials.password,
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Σφάλμα σύνδεσης');
      }

      const data = await response.json();
      // Cookie set by backend as HttpOnly
      localStorage.setItem('is_super_admin', 'true');

      toast.success('Καλωσήρθατε, Super Admin!');
      window.location.assign('/super-admin');
    } catch (error) {
      toast.error(error.message || 'Σφάλμα σύνδεσης');
    } finally {
      setLoading(false);
    }
  };

  // Forced password change screen
  if (showChangePassword) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#004C98] to-[#002855]" data-testid="change-password-page">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
          <div className="text-center mb-6">
            <KeyRound className="w-12 h-12 text-amber-500 mx-auto mb-3" />
            <h1 className="text-2xl font-bold text-slate-800">Αλλαγή Κωδικού</h1>
            <p className="text-sm text-slate-500 mt-1">Πρέπει να ορίσετε νέο κωδικό για λόγους ασφαλείας</p>
          </div>
          <div className="space-y-4">
            <div>
              <Label>Νέος Κωδικός</Label>
              <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                placeholder="Τουλάχιστον 6 χαρακτήρες" className="h-12" data-testid="new-password-input" />
            </div>
            <div>
              <Label>Επιβεβαίωση Κωδικού</Label>
              <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Πληκτρολογήστε ξανά" className="h-12" 
                onKeyDown={e => e.key === 'Enter' && handleChangePassword()}
                data-testid="confirm-password-input" />
            </div>
            <Button onClick={handleChangePassword} disabled={loading}
              className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-white font-semibold"
              data-testid="change-password-btn">
              {loading ? 'Αποθήκευση...' : 'Αποθήκευση & Είσοδος'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Role selection screen
  if (loginMode === 'select') {
    return (
      <div
        className="min-h-screen flex items-center justify-center relative"
        style={{
          backgroundImage: 'url(/login-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundColor: '#0a1e3f',
        }}
        data-testid="login-page"
      >
        {/* v1.0.9.29 · subtle dark overlay to keep the form readable on top of the branded bg */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#1e4d8b]/40 via-[#2a6fb5]/35 to-[#1e4d8b]/45"></div>
        <div key={loginMode} className="relative z-10 w-full max-w-md mx-4 login-fade-in">
          {/* v1.0.9.29 · white logo above the card — dominant, ~320-380px on desktop */}
          <div className="text-center mb-3" data-testid="login-branding">
            <img
              src="/logo-white.png"
              alt="ParkingEvo"
              className="h-56 sm:h-72 md:h-80 lg:h-96 w-auto mx-auto drop-shadow-2xl"
              draggable="false"
              data-testid="login-logo"
            />
            <p className="mt-1 text-sm md:text-base text-blue-100/90 font-medium tracking-wide">
              Σύστημα Διαχείρισης Πάρκινγκ
            </p>
          </div>
          <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-10">
            <div className="text-center mb-6">
              <h1 className="text-2xl font-extrabold text-[#0a1e3f]">Είσοδος</h1>
              <p className="text-sm text-slate-500 mt-1">Επιλέξτε τρόπο σύνδεσης</p>
            </div>

          <div className="space-y-4">
            <Button
              onClick={() => setLoginMode('admin')}
              className="w-full h-16 text-lg font-semibold bg-[#004C98] hover:bg-[#003A75] text-white shadow-lg"
              data-testid="admin-login-btn"
            >
              <Shield className="w-6 h-6 mr-3" />
              Είσοδος ως Διαχειριστής
            </Button>

            <Button
              onClick={() => setLoginMode('user')}
              variant="outline"
              className="w-full h-16 text-lg font-semibold border-2 border-[#004C98] text-[#004C98] hover:bg-[#004C98] hover:text-white shadow-lg"
              data-testid="user-login-btn"
            >
              <UserCircle className="w-6 h-6 mr-3" />
              Είσοδος ως Χρήστης
            </Button>

            <Button
              onClick={() => navigate('/super-admin')}
              variant="outline"
              className="w-full h-14 text-base font-semibold border-2 border-amber-500 text-amber-600 hover:bg-amber-500 hover:text-white shadow-lg"
              data-testid="super-admin-entry-btn"
            >
              <Crown className="w-5 h-5 mr-3" />
              Είσοδος Super Admin
            </Button>
          </div>

        </div>
        </div>
      </div>
    );
  }

  // Super Admin Login
  if (loginMode === 'super_admin') {
    return (
      <div
        className="min-h-screen flex items-center justify-center relative"
        style={{
          backgroundImage: 'url(/login-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundColor: '#0a1e3f',
        }}
        data-testid="super-admin-login-page"
      >
        {/* v1.0.9.31b · same branded background + dark overlay as the role-select screen */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#1e4d8b]/40 via-[#2a6fb5]/35 to-[#1e4d8b]/45"></div>
        <div key={loginMode} className="relative z-10 w-full max-w-md mx-4 login-fade-in">
          {/* v1.0.9.31b · consistent white logo above the card — matches step-1 branding */}
          <div className="text-center mb-3" data-testid="sa-login-branding">
            <img
              src="/logo-white.png"
              alt="ParkingEvo"
              className="h-40 sm:h-52 md:h-56 w-auto mx-auto drop-shadow-2xl"
              draggable="false"
              data-testid="sa-login-logo"
            />
          </div>
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-[#0a1e3f]">Super Admin</h1>
              <p className="text-sm text-slate-500">Κεντρική Διαχείριση Πάρκινγκ</p>
            </div>

          <Button onClick={() => setLoginMode('select')} variant="ghost" className="mb-4 text-slate-600">
            ← Επιστροφή
          </Button>

          <div className="space-y-4">
            <div>
              <Label className="flex items-center gap-2 mb-2"><User size={16} />Όνομα Χρήστη</Label>
              <Input value={credentials.username} onChange={e => setCredentials({...credentials, username: e.target.value})}
                placeholder="superadmin" className="h-12" data-testid="sa-username-input" />
            </div>
            <div>
              <Label className="flex items-center gap-2 mb-2"><KeyRound size={16} />Κωδικός</Label>
              <Input type="password" value={credentials.password} onChange={e => setCredentials({...credentials, password: e.target.value})}
                placeholder="Κωδικός" className="h-12"
                onKeyDown={e => e.key === 'Enter' && handleSuperAdminLogin()}
                data-testid="sa-password-input" />
            </div>
            <Button onClick={handleSuperAdminLogin} disabled={loading}
              className="w-full h-12 bg-gradient-to-r from-amber-500 to-red-600 hover:from-amber-600 hover:to-red-700 text-white font-semibold"
              data-testid="sa-login-btn">
              {loading ? 'Σύνδεση...' : 'Σύνδεση Super Admin'}
            </Button>
            <button onClick={() => navigate('/forgot-password?sa=1')} className="w-full text-center text-xs text-slate-400 hover:text-amber-500 hover:underline mt-2" data-testid="sa-forgot-password-link">
              Ξέχασα τον κωδικό μου
            </button>
          </div>
        </div>
        </div>
      </div>
    );
  }

  // Admin / User Login (with tenant code)
  return (
    <div
      className="min-h-screen flex items-center justify-center relative"
      style={{
        backgroundImage: 'url(/login-bg.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: '#0a1e3f',
      }}
      data-testid="login-page"
    >
      {/* v1.0.9.31b · same branded background + dark overlay as the role-select screen */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#1e4d8b]/40 via-[#2a6fb5]/35 to-[#1e4d8b]/45"></div>
      <div key={loginMode} className="relative z-10 w-full max-w-md mx-4 login-fade-in">
        {/* v1.0.9.31b · consistent white logo above the card — matches step-1 branding */}
        <div className="text-center mb-3" data-testid="login-form-branding">
          <img
            src="/logo-white.png"
            alt="ParkingEvo"
            className="h-40 sm:h-52 md:h-56 w-auto mx-auto drop-shadow-2xl"
            draggable="false"
            data-testid="login-form-logo"
          />
        </div>
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-10">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-extrabold text-[#0a1e3f]">
              {loginMode === 'admin' ? 'Σύνδεση Διαχειριστή' : 'Σύνδεση Χρήστη'}
            </h1>
          </div>

        <Button onClick={() => { setLoginMode('select'); setCredentials({username:'',password:'',tenant_code:''}); }}
          variant="ghost" className="mb-4 text-slate-600 hover:text-[#004C98]">
          ← Επιστροφή
        </Button>

        <div className="space-y-4">
          {/* Tenant Code (License Key) */}
          <div>
            <Label className="flex items-center gap-2 mb-2">
              <KeySquare size={16} />
              Κωδικός Πάρκινγκ
            </Label>
            <Input
              value={credentials.tenant_code}
              onChange={e => setCredentials({...credentials, tenant_code: e.target.value.toUpperCase()})}
              placeholder="π.χ. PARK-XXXX-XXXX-XXXX"
              className="h-12 font-mono tracking-wider"
              data-testid="tenant-code-input"
            />
            <p className="text-xs text-slate-400 mt-1">Ο κωδικός parking είναι υποχρεωτικός και δίνεται από τον superadmin / διαχειριστή.</p>
            <label className="flex items-center gap-2 mt-2 cursor-pointer select-none">
              <input type="checkbox" checked={rememberCode} onChange={e => setRememberCode(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300" data-testid="remember-code-checkbox" />
              <span className="text-xs text-slate-500 flex items-center gap-1"><Save size={12} /> Αποθήκευση κωδικού parking</span>
            </label>
          </div>

          <div>
            <Label className="flex items-center gap-2 mb-2">
              <User size={16} />
              Όνομα Χρήστη
            </Label>
            <Input
              value={credentials.username}
              onChange={e => setCredentials({...credentials, username: e.target.value})}
              placeholder={loginMode === 'admin' ? 'π.χ. admin' : 'π.χ. operator1'}
              className="h-12"
              data-testid="username-input"
            />
          </div>

          <div>
            <Label className="flex items-center gap-2 mb-2">
              <KeyRound size={16} />
              Κωδικός
            </Label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={credentials.password}
                onChange={e => setCredentials({...credentials, password: e.target.value})}
                placeholder="Εισάγετε τον κωδικό σας"
                className="h-12 pr-12"
                onKeyDown={e => e.key === 'Enter' && handlePasswordLogin()}
                data-testid="password-input"
              />
              <Button type="button" variant="ghost"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </Button>
            </div>
          </div>

          <Button
            onClick={handlePasswordLogin}
            disabled={loading}
            className={`w-full h-14 font-semibold text-lg text-white shadow-lg ${
              loginMode === 'admin' ? 'bg-[#004C98] hover:bg-[#003A75]' : 'bg-[#E5A00D] hover:bg-[#C28500]'
            }`}
            data-testid="password-login-button"
          >
            {loading ? 'Σύνδεση...' : 'Σύνδεση'}
          </Button>

          {loginMode === 'user' && (
            <p className="text-center text-xs text-slate-500">
              Ζητήστε τον Κωδικό Πάρκινγκ και τα στοιχεία σύνδεσης από τον διαχειριστή
            </p>
          )}
          {loginMode === 'admin' && (
            <button onClick={() => navigate('/forgot-password')} className="w-full text-center text-xs text-[#004C98] hover:underline mt-1" data-testid="forgot-password-link">
              Ξέχασα τον κωδικό μου
            </button>
          )}
          {loginMode === 'admin' && (
            <button onClick={() => handleForgotKey()} className="w-full text-center text-xs text-slate-400 hover:text-[#004C98] hover:underline mt-1" data-testid="forgot-key-link">
              Ξέχασα το κλειδί μου
            </button>
          )}
        </div>
      </div>
      </div>
      {/* v1.0.9.33 · Legal footer (Viva source checklist items 2-5) */}
      <footer className="py-4 px-4 text-center text-xs text-slate-400" data-testid="login-legal-footer">
        <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 mb-1">
          <a href="/terms"   className="hover:text-[#004C98]" data-testid="login-footer-terms">Όροι Χρήσης</a>
          <span className="text-slate-300">·</span>
          <a href="/privacy" className="hover:text-[#004C98]" data-testid="login-footer-privacy">Πολιτική Απορρήτου</a>
          <span className="text-slate-300">·</span>
          <a href="/contact" className="hover:text-[#004C98]" data-testid="login-footer-contact">Επικοινωνία</a>
        </div>
        <div>
          Payments powered by{' '}
          <a href="https://www.viva.com" target="_blank" rel="noreferrer noopener"
             className="text-[#F26B21] hover:underline font-medium">viva.com</a>
        </div>
      </footer>
    </div>
  );
}
